import React, { useState, useEffect } from 'react';
import { FileText, Plus, CheckCircle, Clock, X } from 'lucide-react';
import { employeeService } from '../../services/employeeService';

export default function EmployeeDocumentsCard({ employeeUuid, canAdd = true }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    document_type: '',
    document_number: '',
    issuing_authority: '',
    issue_date: '',
    expiry_date: '',
    file_name: '',
    file_path: '',
    mime_type: 'application/pdf',
    file_size: 102456
  });

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await employeeService.getEmployeeDocuments(employeeUuid);
      setDocuments(res.data || []);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeUuid) fetchDocuments();
  }, [employeeUuid]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        file_name: `${formData.document_type || 'document'}.pdf`,
        file_path: `employee/documents/${formData.document_type || 'doc'}.pdf`
      };

      await employeeService.addEmployeeDocument(employeeUuid, payload);
      setIsModalOpen(false);
      fetchDocuments();
      
      setFormData({
        document_type: '',
        document_number: '',
        issuing_authority: '',
        issue_date: '',
        expiry_date: '',
        file_name: '',
        file_path: '',
        mime_type: 'application/pdf',
        file_size: 102456
      });
    } catch (err) {
      console.error('Error saving document:', err);
      alert('Failed to save document.');
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-4">
      
      {/* Card Header matching Personal & Contact Info Cards */}
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-800">Employee Documents</h3>
        
        {/* Add Button - Strictly visible ONLY when canAdd is explicitly true */}
        {canAdd === true && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Document
          </button>
        )}
      </div>

      {/* Documents List in Grid Format */}
      {loading ? (
        <p className="text-xs text-gray-500 text-center py-4">Loading documents...</p>
      ) : documents.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">No documents uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => (
            <div key={doc.uuid} className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-start justify-between gap-3 shadow-2xs">
              <div className="flex items-start gap-3 w-full">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg border border-blue-100 shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Dynamic Document Type as Label/Heading */}
                  <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide truncate">
                    {doc.document_type || 'Unnamed Document'}
                  </h4>
                  
                  <div className="mt-2 space-y-1 text-xs">
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-400">Number:</span>{' '}
                      <span className="font-mono text-gray-800">{doc.document_number || 'N/A'}</span>
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-400">Authority:</span>{' '}
                      <span className="text-gray-800">{doc.issuing_authority || 'N/A'}</span>
                    </p>
                    {doc.issue_date && (
                      <p className="text-gray-600">
                        <span className="font-medium text-gray-400">Issued:</span>{' '}
                        <span className="text-gray-800">{doc.issue_date}</span>
                      </p>
                    )}
                    {doc.expiry_date && (
                      <p className="text-gray-600">
                        <span className="font-medium text-gray-400">Expires:</span>{' '}
                        <span className="text-gray-800">{doc.expiry_date}</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      doc.verification_status === 'verified' 
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                        : 'bg-amber-50 text-amber-600 border border-amber-200'
                    }`}>
                      {doc.verification_status === 'verified' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {doc.verification_status || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Document Modal */}
      {isModalOpen && canAdd === true && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-in fade-in duration-200">
            
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h4 className="text-sm font-bold text-gray-900">Add New Document</h4>
              <button onClick={() => setIsModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Document Type</label>
                <input 
                  type="text" 
                  name="document_type" 
                  placeholder="e.g. passport, pan, license" 
                  value={formData.document_type} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Document Number</label>
                <input 
                  type="text" 
                  name="document_number" 
                  placeholder="Enter document number" 
                  value={formData.document_number} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Issuing Authority</label>
                <input 
                  type="text" 
                  name="issuing_authority" 
                  placeholder="e.g. Income Tax Dept, Passport Office" 
                  value={formData.issuing_authority} 
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Issue Date</label>
                  <input 
                    type="date" 
                    name="issue_date" 
                    value={formData.issue_date} 
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Expiry Date</label>
                  <input 
                    type="date" 
                    name="expiry_date" 
                    value={formData.expiry_date} 
                    onChange={handleChange}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm cursor-pointer"
                >
                  Save Document
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}